import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, Trophy } from "lucide-react";
import { Logo } from "@/components/Logo";

const medal = (rank) => rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`;

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/referrals/leaderboard?limit=25").then((r) => setRows(r.data.leaderboard || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFDFB]">
      <header className="border-b border-[#E2E8E4]">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
          </Link>
          <Link to="/" className="text-xs text-[#5C6661] inline-flex items-center gap-1 hover:text-[#121413]" data-testid="back-home"><ArrowLeft className="w-3 h-3" /> Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="overline">Community</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight mt-2 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-[#2D4A3E]" strokeWidth={1.5} /> Top referrers
        </h1>
        <p className="text-sm text-[#5C6661] mt-3 max-w-xl">These are the people who&apos;ve invited the most friends to JobKnocker and racked up the most free months of Pro. Want in? Grab your code in <Link to="/app/settings" className="text-[#2D4A3E] font-medium underline">Settings</Link>.</p>

        <Card className="mt-10 border-[#E2E8E4] overflow-hidden" data-testid="leaderboard-card">
          {loading ? (
            <div className="p-10 text-center text-sm text-[#5C6661] animate-soft-pulse">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#5C6661]" data-testid="leaderboard-empty">
              No referrals converted yet. Be the first — <Link to="/app/settings" className="text-[#2D4A3E] font-medium underline">invite a friend</Link>.
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8E4]">
              {rows.map((r) => (
                <div key={r.rank} className="flex items-center justify-between px-6 py-4" data-testid={`leaderboard-row-${r.rank}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-display font-bold ${r.rank <= 3 ? "bg-[#E7F0EB] text-[#2D4A3E]" : "bg-[#F2F4F3] text-[#5C6661]"}`}>
                      <span>{medal(r.rank)}</span>
                    </div>
                    <div>
                      <div className="font-medium text-[#121413]">{r.name}</div>
                      <div className="text-xs text-[#5C6661]">{r.months_earned} free month{r.months_earned === 1 ? "" : "s"} earned</div>
                    </div>
                  </div>
                  {r.rank === 1 && <Badge className="bg-[#2D4A3E] text-white border-transparent"><Crown className="w-3 h-3 mr-1" /> Top referrer</Badge>}
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="mt-6 text-xs text-[#5C6661]">Names are shown as first name + last initial. You can opt out from <Link to="/app/settings" className="underline">Settings</Link>.</div>
      </main>
    </div>
  );
}
